import React from 'react';
import { Modal, Form, Button } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import PlanAccountReportForm from '../PlanAccountReportForm';
import { ModalProps } from '@/types/ModalProps';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { useDefaultFormErrorNotification } from '@/hooks';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import { useGeneratePlanAccountsReport } from '@/core/domain/reports/stock/Report.hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';

function PlanAccountReportModal({ visible, handleVisible }: ModalProps) {
  const intl = useIntl();

  const [form] = Form.useForm();

  const mutation = useGeneratePlanAccountsReport();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { fileLoading, setFileLoading, setFileUnloading, FILE_FORMAT } =
    useFileLoading();
  const report = useReportGenerate();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const values = await form.validateFields();
      const obj = {
        ...values,
        active: values.active
          ? values.active === 'true'
            ? true
            : false
          : null,
        format: FILE_FORMAT[format],
      };

      const result = await mutation.mutateAsync(obj);

      if (callback) {
        callback(result);
      }

      handleVisible();
    } catch (err) {
      sendFormErrorNotification(err);
    }
    setFileUnloading(format);
  };

  return (
    <Modal
      width={680}
      open={visible}
      onCancel={handleVisible}
      title={intl.formatMessage({ id: 'planAccounts.modal.report.title' })}
      footer={
        <SpaceForButton justifyContent="end">
          <Button
            type="primary"
            key="viewReport"
            block
            loading={fileLoading.REPORT_VIEW}
            onClick={() =>
              handleGenerateReport('REPORT_VIEW', (result) =>
                report.openFile({
                  result,
                  fileName: 'Planos de Contas.pdf',
                  fileType: 'application/pdf',
                }),
              )
            }
          >
            <FormattedMessage id="planAccounts.modal.report.buttons.viewReport" />
          </Button>

          <Button
            type="primary"
            key="PDF"
            block
            onClick={() =>
              handleGenerateReport('PDF', (result) =>
                report.downloadFile({
                  result,
                  fileName: 'Planos de Contas.pdf',
                }),
              )
            }
            loading={fileLoading.PDF}
          >
            <FormattedMessage id="planAccounts.modal.report.buttons.pdf" />
          </Button>

          <Button
            type="primary"
            key="CSV"
            block
            onClick={() =>
              handleGenerateReport('CSV', (result) =>
                report.downloadFile({
                  result,
                  fileName: 'Plano de Contas.csv',
                }),
              )
            }
          >
            <FormattedMessage id="planAccounts.modal.report.buttons.csv" />
          </Button>
        </SpaceForButton>
      }
    >
      <PlanAccountReportForm form={form} />
    </Modal>
  );
}

export default PlanAccountReportModal;
